import { useState } from "react";
import { ChevronDownIcon, PlusIcon } from "lucide-react";

import { CampoDeCastigo } from "@/components/generacion/CampoDeCastigo";
import { FormularioDeLineaManual } from "@/components/generacion/FormularioDeLineaManual";
import { LineaDeValorizacion } from "@/components/generacion/LineaDeValorizacion";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Etiqueta } from "@/components/ui/etiqueta";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useFijarCastigos } from "@/hooks/useValorizacion";
import { getApiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import {
  ETIQUETA_ESTADO_DE_LINEA,
  TONO_DE_ESTADO_DE_LINEA,
  estadoDelRubro,
  formatearPrecio,
  propuestaDelRubro,
  textoOGuion,
  type RubroValorizado,
} from "@/lib/valorizacion";
import { cn } from "@/lib/utils";

/**
 * Las cinco columnas del cuadro «Propuesta de valor por rubro» del diseño:
 * rubro, propuesta · origen, valor final, estado y acción. El encabezado y cada
 * fila comparten esta plantilla para que no se desalineen.
 */
export const COLUMNAS_DEL_CUADRO =
  "grid-cols-[minmax(0,1.1fr)_minmax(0,1.3fr)_8rem_5.75rem_8.25rem]";

interface BloqueDeRubroProps {
  informeId: number;
  rubro: RubroValorizado;
  /** El castigo global vigente, para decir qué usa el rubro cuando no tiene uno propio. */
  castigoGlobal: number | null;
}

const plural = (n: number, singular: string, pluralForma: string) => `${n} ${n === 1 ? singular : pluralForma}`;

/** «3 líneas · 2 con precio · 1 sin precio». Lo que vale cero no se nombra. */
function contadores(rubro: RubroValorizado): string {
  if (rubro.lineas.length === 0) return "Sin líneas";

  return [
    plural(rubro.lineas.length, "línea", "líneas"),
    rubro.lineas_valorizadas > 0 ? `${rubro.lineas_valorizadas} con precio` : null,
    rubro.lineas_sin_precio > 0 ? `${rubro.lineas_sin_precio} sin precio` : null,
    rubro.lineas_sin_cantidad > 0 ? `${rubro.lineas_sin_cantidad} sin cantidad` : null,
    rubro.lineas_incluidas > 0 ? plural(rubro.lineas_incluidas, "incluida", "incluidas") : null,
  ]
    .filter((x): x is string => x !== null)
    .join(" · ");
}

/**
 * Un rubro, como una fila del cuadro de valor del diseño: su propuesta, su valor
 * final en UF, su estado y, al abrirlo, sus líneas.
 *
 * EL MODELO ES POR LÍNEA y el diseño dibuja un valor por rubro, así que la fila
 * RESUME: la propuesta del rubro solo aparece cuando TODAS sus líneas tienen
 * una (en UF), el valor final es la suma que calculó el servidor y el estado es
 * el de la línea más atrasada. Corregir un precio se hace en la línea, abajo.
 *
 * Arranca abierto si todavía hay líneas por fijar; uno resuelto queda cerrado
 * para que la pantalla muestre lo que falta y no lo que ya está.
 */
export function BloqueDeRubro({ informeId, rubro, castigoGlobal }: BloqueDeRubroProps) {
  const [abierto, setAbierto] = useState(rubro.lineas_sin_precio > 0 || rubro.lineas_sin_cantidad > 0);
  const [agregando, setAgregando] = useState(false);
  const castigos = useFijarCastigos(informeId);
  const sinValor = rubro.valor_formateado.uf === null;
  const estado = estadoDelRubro(rubro);
  const propuesta = propuestaDelRubro(rubro);

  return (
    <section aria-label={`Rubro ${rubro.etiqueta}`} className="border-divisor-fuerte border-t-[1.5px] first:border-t-0">
      <Collapsible open={abierto} onOpenChange={setAbierto}>
        <div className={cn("grid items-center gap-x-3 px-5 py-2.5", COLUMNAS_DEL_CUADRO)}>
          <div className="min-w-0">
            <CollapsibleTrigger asChild>
              <button type="button" className="flex items-center gap-1.5 text-left">
                <span className="text-ink text-[0.8125rem] font-bold">{rubro.etiqueta}</span>
                <ChevronDownIcon
                  aria-hidden
                  className={cn("text-ink-medio size-3.5 shrink-0 transition-transform", abierto && "rotate-180")}
                />
              </button>
            </CollapsibleTrigger>
            <div className="text-ink-medio font-mono text-[0.6875rem] font-semibold">{contadores(rubro)}</div>
            {rubro.castigo.pct === null ? (
              <div className="text-ink-medio text-[0.6875rem] font-semibold">
                Liquidación pendiente: falta el castigo
              </div>
            ) : (
              <div className="text-ink-medio text-[0.6875rem] font-semibold">
                <span>Liquidación (castigo {rubro.castigo.pct.toLocaleString("es-CL")} %)</span>{" "}
                <span className="font-mono">{textoOGuion(rubro.liquidacion_formateada.uf)}</span>
              </div>
            )}
          </div>

          <div className="min-w-0">
            {propuesta ? (
              <>
                <div className="text-ink font-mono text-[0.8125rem] font-bold">
                  {formatearPrecio(propuesta.uf, "UF")}
                </div>
                <div className="text-ink-secundario text-[0.71875rem] font-semibold">{propuesta.origen}</div>
              </>
            ) : (
              <>
                <div className="text-ink-hint">–</div>
                <div className="text-danger-texto text-[0.71875rem] font-semibold">
                  Sin propuesta en todas las líneas: ingresa el valor
                </div>
              </>
            )}
          </div>

          <div
            className={cn(
              "flex h-11 items-center justify-end rounded-xl border-[1.5px] px-3.5 font-mono text-[0.8125rem] font-bold",
              estado === "propuesta" ? "border-primary" : "border-input",
              sinValor && !(propuesta && estado === "propuesta") && "text-ink-ghost",
            )}
          >
            {sinValor ? (
              propuesta && estado === "propuesta" ? (
                formatearPrecio(propuesta.uf, "UF")
              ) : (
                <span className="font-sans font-semibold">Sin valor</span>
              )
            ) : (
              rubro.valor_formateado.uf
            )}
          </div>

          <div>
            <Etiqueta tono={TONO_DE_ESTADO_DE_LINEA[estado]}>{ETIQUETA_ESTADO_DE_LINEA[estado]}</Etiqueta>
          </div>

          <div className="flex justify-end">
            <CollapsibleTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className="border-input h-auto rounded-[0.625rem] px-3 py-1.5 text-xs shadow-none"
              >
                {abierto ? "Ocultar líneas" : "Ver líneas"}
              </Button>
            </CollapsibleTrigger>
          </div>
        </div>

        <CollapsibleContent>
          <div className="bg-superficie-tenue border-divisor-fuerte space-y-3 border-t-[1.5px] px-5 py-3">
            <p className="text-ink-secundario flex flex-wrap items-center gap-x-3 font-mono text-xs font-semibold">
              <span className="font-sans font-bold">Total del rubro en pesos y dólares</span>
              <span>{textoOGuion(rubro.valor_formateado.clp)}</span>
              <span>{textoOGuion(rubro.valor_formateado.usd)}</span>
            </p>
            {rubro.nota ? <p className="text-ink-secundario text-xs font-semibold">{rubro.nota}</p> : null}

            {rubro.lineas.length === 0 ? (
              <p className="text-ink-medio text-xs font-semibold">Este rubro no tiene líneas todavía.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead variant="versalitas">Línea</TableHead>
                    <TableHead variant="versalitas">Propuesta · origen</TableHead>
                    <TableHead variant="versalitas">Valor final</TableHead>
                    <TableHead variant="versalitas">Estado</TableHead>
                    <TableHead variant="versalitas" className="text-right">
                      Total
                    </TableHead>
                    <TableHead>
                      <span className="sr-only">Acciones</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rubro.lineas.map((linea) => (
                    <LineaDeValorizacion
                      // Al guardar cambia la llave y la fila vuelve a leer lo guardado.
                      key={`${linea.id}:${linea.precio?.fijado_en ?? "-"}:${linea.precio?.valor ?? "-"}:${linea.precio?.moneda ?? "-"}:${linea.incluido_en ?? "-"}`}
                      informeId={informeId}
                      linea={linea}
                    />
                  ))}
                </TableBody>
              </Table>
            )}

            {agregando ? (
              <FormularioDeLineaManual informeId={informeId} rubro={rubro.rubro} onListo={() => setAgregando(false)} />
            ) : (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="border-input h-auto rounded-[0.625rem] px-3 py-1.5 text-xs shadow-none"
                aria-label={`Agregar línea a ${rubro.etiqueta}`}
                onClick={() => setAgregando(true)}
              >
                <PlusIcon aria-hidden /> Agregar línea
              </Button>
            )}

            <div className="border-divisor-fuerte flex flex-wrap items-center gap-3 border-t-[1.5px] pt-3">
              <span className="text-ink text-xs font-bold">Castigo de liquidación de este rubro</span>
              <CampoDeCastigo
                // El campo vuelve a leer lo guardado cuando cambia.
                key={rubro.castigo.propio ?? "sin-castigo"}
                etiqueta={`Castigo de ${rubro.etiqueta} (%)`}
                etiquetaDelBoton={`Guardar castigo de ${rubro.etiqueta}`}
                valor={rubro.castigo.propio}
                placeholder={castigoGlobal === null ? "Sin castigo" : `Usa el global (${castigoGlobal.toLocaleString("es-CL")} %)`}
                guardando={castigos.isPending}
                onGuardar={(valor) =>
                  castigos.mutate(
                    { por_rubro: { [rubro.rubro]: valor } },
                    { onError: (falla) => toast.error(getApiErrorMessage(falla, "No pudimos guardar el castigo.")) },
                  )
                }
              />
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
}
